import { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '../google.js';
import { errorDetail, track } from '../telemetry.js';
import { reportUnexpected } from '../observed-request.js';
import { cn } from '../lib/cn.js';

export interface SelectedLocation {
  label: string;
  lat: number;
  lng: number;
  source: 'search' | 'pin';
}

const NZ_BOUNDS: google.maps.LatLngBoundsLiteral = {
  south: -47.5,
  west: 165.2,
  north: -34,
  east: 179.5
};

export function PlaceSearch({
  label,
  placeholder,
  onSelect,
  resetKey = 0,
  autoFocus = false,
  inline = false,
  initialValue = ''
}: {
  label: string;
  placeholder: string;
  onSelect: (location: SelectedLocation) => void;
  resetKey?: number;
  autoFocus?: boolean;
  inline?: boolean;
  initialValue?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const [selectionError, setSelectionError] = useState(false);
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    if (!apiKey) {
      setStatus('missing');
      return;
    }
    let disposed = false;
    let selection = 0;
    let focusFrame: number | undefined;
    let autocomplete: HTMLElement | undefined;
    const handleSelect = async (event: Event) => {
      const request = ++selection;
      setSelectionError(false);
      try {
        const selected = event as google.maps.places.PlacePredictionSelectEvent;
        const place = selected.placePrediction.toPlace();
        await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] });
        if (disposed || request !== selection) return;
        if (!place.location) throw new Error('Selected place has no location.');
        onSelect({
          label: place.displayName ?? place.formattedAddress ?? 'Selected location',
          lat: place.location.lat(),
          lng: place.location.lng(),
          source: 'search'
        });
      } catch (error) {
        if (disposed || request !== selection) return;
        track('maps-error', { ...errorDetail(error), detail: { component: 'place-search', action: 'select' } });
        reportUnexpected(error, { reason: 'map' });
        setSelectionError(true);
      }
    };

    void (async () => {
      try {
        const googleMaps = await loadGoogleMaps(apiKey);
        if (disposed || !containerRef.current) return;
        const element = new googleMaps.maps.places.PlaceAutocompleteElement();
        element.placeholder = placeholder;
        element.value = initialValue;
        element.setAttribute('aria-label', label);
        element.noInputIcon = true;
        if (inline) {
          element.noClearButton = true;
        }
        element.locationBias = NZ_BOUNDS;
        element.addEventListener('gmp-select', handleSelect);
        containerRef.current.replaceChildren(element);
        autocomplete = element;
        setStatus('ready');
        if (autoFocus) {
          element.tabIndex = 0;
          focusFrame = requestAnimationFrame(() => element.focus());
        }
      } catch (error) {
        track('maps-error', { ...errorDetail(error), detail: { ...errorDetail(error).detail, component: 'place-search' } });
        reportUnexpected(error, { reason: 'map' });
        if (!disposed) setStatus('error');
      }
    })();

    return () => {
      disposed = true;
      if (focusFrame !== undefined) cancelAnimationFrame(focusFrame);
      autocomplete?.removeEventListener('gmp-select', handleSelect);
      containerRef.current?.replaceChildren();
    };
  }, [apiKey, autoFocus, initialValue, inline, label, onSelect, placeholder, resetKey]);

  return (
    <div className={cn('place-search', inline && 'place-search-inline')}>
      {!inline ? <label>{label}</label> : null}
      <div ref={containerRef} className={cn('autocomplete-host', status !== 'ready' && 'autocomplete-not-ready')} />
      {status === 'missing' ? <p className="field-note">Add a restricted <code>VITE_GOOGLE_MAPS_API_KEY</code> to enable search.</p> : null}
      {status === 'error' ? <p className="field-note field-error">Google location search could not load.</p> : null}
      {selectionError ? <p className="field-note field-error" role="alert">That place could not be loaded. Try selecting it again or choose another place.</p> : null}
    </div>
  );
}
